import {
  CreateEscalationInterface, EscalationInterface,
  EscalationRepositoryInterface,
  EscalationResponseInterface,
} from '@/services/QA/escalation/interfaces';
import { AxiosInstance } from 'axios';
import { ChatReviewFormDataInterface, ChatReviewInterface } from '@/services/QC-V2/chat/interfaces';

export enum EscalationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum EscalationType {
  CHAT = 'chat',
  TICKET = 'ticket',
}

export enum EscalationStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in-progress',
  REVIEWED = 'reviewed',
}

export default class EscalationRepository implements EscalationRepositoryInterface {
  constructor(
    private axios: AxiosInstance,
  ) {
  }

  public async createChatEscalation(
    chatId: string,
    agents: string[],
    data: CreateEscalationInterface,
  ): Promise<void> {
    await this.axios.post(`/qa-controller/escalation/chat/${chatId}`, { agents, escalationDTO: data });
  }

  public async createTicketEscalation(
    ticketId: number,
    agents: string[],
    data: CreateEscalationInterface,
  ): Promise<void> {
    await this.axios.post(`/qa-controller/escalation/ticket/${ticketId}`, { agents, escalationDTO: data });
  }

  public async updateEscalation(escalationId: number, data: CreateEscalationInterface): Promise<void> {
    await this.axios.patch(`/qa-controller/escalation/${escalationId}`, data);
  }

  public async getEscalation(escalationId: number): Promise<EscalationResponseInterface> {
    const res = await this.axios.get(`/qa-controller/escalation/${escalationId}`);

    return res.data;
  }

  public async getAllEscalations(
    showReviewed: boolean,
    currentPage: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[EscalationInterface[], number]> {
    const direction = sortDesc ? 'DESC' : 'ASC';
    const res = await this.axios.get(
      `/qa-controller/escalation?reviewed=${showReviewed}&page=${currentPage}&sortBy=${sortBy}&direction=${direction}`,
    );

    return res.data;
  }

  public async getEscalationByResourceId(resourceId: string): Promise<EscalationInterface> {
    const res = await this.axios.get(`/qa-controller/escalation/resource/${resourceId}`);

    return res.data;
  }

  public async reviewEscalation(
    escalationId: number,
    agents: string[],
    scores: { [scoreTypeId: number]: number },
    tags: number[],
    chatId?: string | undefined,
    ticketId?: number | undefined,
  ): Promise<void> {
    await this.axios.patch(
      `/qa-controller/escalation/${escalationId}`,
      {
        ticketId,
        chatId,
        reviewData: { agents, scores, tags },
      },
    );
  }

  public async changeEscalationStatus(escalationId: number, status: EscalationStatus): Promise<void> {
    await this.axios.patch(`/qa-controller/escalation/${escalationId}/status`, { status });
  }

  public async searchEscalations(
    currentPage: number,
    search: string,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[EscalationInterface[], number]> {
    const direction = sortDesc ? 'DESC' : 'ASC';
    const baseUrl = `/qa-controller/escalation/search/${encodeURIComponent(search)}`;
    const urlParams = `?page=${currentPage}&sortBy=${sortBy}&direction=${direction}`;
    const res = await this.axios.get(`${baseUrl}${urlParams}`);

    return res.data;
  }
}
